import { IdpAuthError } from './provider';

/**
 * As chamadas do app para o IDP nao passam pelo `services/request.ts`.
 *
 * Aquele axios carrega o interceptor que anexa o Bearer da sessao do Sapron e
 * manda o usuario para `/login` em qualquer 401 — os dois seriam errados aqui:
 * o IDP nao quer o token do Sapron, e um 401 do token endpoint e resposta
 * normal de refresh vencido, nao motivo para derrubar a navegacao.
 */

/** Discovery e JWKS com timeout curto, como o design fechou (5s). */
export const IDP_TIMEOUT_MS = 5000;

export async function fetchJson<T>(
  url: string,
  init: RequestInit & { readonly timeoutMs?: number; readonly fetchImpl?: typeof fetch },
): Promise<T> {
  const { timeoutMs = IDP_TIMEOUT_MS, fetchImpl = fetch, ...rest } = init;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetchImpl(url, { ...rest, signal: controller.signal });
    if (!response.ok) {
      // O corpo de erro do Keycloak e `{error, error_description}` e nao carrega
      // segredo; e o que diz `invalid_grant` de `invalid_client`.
      const body = await response.text().catch(() => '');
      throw new IdpAuthError(
        'http_erro',
        `IDP respondeu ${response.status} em ${url}: ${body.slice(0, 200)}`,
      );
    }
    return (await response.json()) as T;
  } catch (error) {
    if (error instanceof IdpAuthError) throw error;
    throw new IdpAuthError('http_falhou', `Falha ao falar com o IDP em ${url}.`, { cause: error });
  } finally {
    clearTimeout(timer);
  }
}

/** Erro OAuth (`{error, error_description}`) preservado, que o refresh precisa distinguir. */
export interface OAuthErrorBody {
  readonly error?: string;
  readonly error_description?: string;
}

export async function postForm<T>(
  url: string,
  params: Record<string, string>,
  init: { readonly timeoutMs?: number; readonly fetchImpl?: typeof fetch } = {},
): Promise<T> {
  const { timeoutMs = IDP_TIMEOUT_MS, fetchImpl = fetch } = init;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetchImpl(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(params).toString(),
      signal: controller.signal,
    });
    const payload: unknown = await response.json().catch(() => ({}));
    if (!response.ok) {
      const oauth = payload as OAuthErrorBody;
      throw new IdpAuthError(
        oauth.error ?? 'token_endpoint_erro',
        `Token endpoint respondeu ${response.status}: ${oauth.error ?? 'sem codigo'}.`,
      );
    }
    return payload as T;
  } catch (error) {
    if (error instanceof IdpAuthError) throw error;
    throw new IdpAuthError('http_falhou', `Falha ao falar com o IDP em ${url}.`, { cause: error });
  } finally {
    clearTimeout(timer);
  }
}
